import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export function Login({
  onLogin,
}: {
  onLogin: (email: string, password: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await onLogin(email, password);
    } catch (err: any) {
      setError(err.message || t("auth.loginError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-navy-950">
      {/* Left — Branding panel */}
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center hero-grid noise-bg overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900/50 to-navy-950/90" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[400px] bg-amber-500/6 rounded-full blur-[100px]" />
        <div className="relative z-10 px-16 max-w-lg">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
              <span className="text-navy-950 font-bold text-lg" style={{ fontFamily: "var(--font-display)" }}>K</span>
            </div>
            <span className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              Kverna
            </span>
          </div>
          <h2 className="text-3xl font-bold text-white leading-snug mb-4" style={{ fontFamily: "var(--font-display)" }}>
            {t("auth.brandingTitle1")}{" "}
            <span className="text-amber-400">{t("auth.brandingTitle2")}</span>
          </h2>
          <p className="text-navy-400 leading-relaxed">
            {t("auth.brandingDesc")}
          </p>
        </div>
      </div>

      {/* Right — Login form */}
      <div className="flex-1 flex items-center justify-center px-6 bg-white">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
              <span className="text-navy-950 font-bold text-sm" style={{ fontFamily: "var(--font-display)" }}>K</span>
            </div>
            <span className="text-xl font-bold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              Kverna
            </span>
          </div>

          <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: "var(--font-display)" }}>
            {t("auth.login")}
          </h1>
          <p className="text-navy-400 text-sm mb-8">
            {t("auth.loginSubtitle")}
          </p>

          {error && (
            <div className="bg-rose-50 text-rose-600 text-sm px-4 py-3 rounded-lg mb-6 border border-rose-100">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="input-label">{t("auth.email")}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <label className="input-label">{t("auth.password")}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="input"
              />
            </div>

            <button type="submit" disabled={loading} className="btn btn-primary w-full py-3">
              {loading ? t("common.loading") : t("auth.login")}
            </button>
          </form>

          <p className="text-sm text-center text-navy-400 mt-8">
            {t("auth.noAccount")}{" "}
            <Link to="/register" className="text-amber-600 font-medium hover:text-amber-700 transition-colors">
              {t("auth.register")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
